import React, { useState } from "react";
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Badge,
  Button,
  Segmented,
  Table,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";

const { Content } = Layout;
const { Title, Text } = Typography;

const DonationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");

  const donationData = location.state?.donationData;
  const isActive = donationData?.status === "Active";

  // ✅ Dummy Donated Members Data
  const donatedMembers = [
    {
      name: "Ravi Kumar",
      phone: "9876543210",
      email: "ravi@gmail.com",
      amount: 1000,
      date: "03/25/2026",
    },
    {
      name: "Priya Sharma",
      phone: "9123456780",
      email: "priya@gmail.com",
      amount: 2500,
      date: "03/26/2026",
    },
    {
      name: "Arun S",
      phone: "9001122334",
      email: "arun@gmail.com",
      amount: 500,
      date: "03/27/2026",
    },
  ];

  if (!donationData) {
    return <div className="p-10 text-center">No donation data found</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Button
        icon={<LeftOutlined />}
        type="link"
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        Back
      </Button>

      <Layout>
        <Content>
          {/* HERO SECTION (Reduced Height) */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            <img
              src={donationData.image}
              alt="donation"
                style={{
                width: "100%",
                height: 200,
                objectFit: "cover",
              }}
            />

            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-4">
              <div className="flex justify-between items-center">
                <Title level={3} className="!text-white !mb-0">
                  {donationData.title}
                </Title>
                <Badge
                  count={donationData.status}
                  style={{
                    backgroundColor: isActive ? "#52c41a" : "#ff4d4f",
                  }}
                />
              </div>

              <div className="mt-1 text-white text-sm">
                📍 {donationData.location}
              </div>

              {Number(donationData.amount) > 0 && (
                <div className="text-white font-medium text-sm">
                  💰 ₹ {donationData.amount}
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="event-details-tabs-container mt-3">
            <Segmented
              options={[
                { label: "Donation Details", value: "details" },
                { label: "Donated Members", value: "members" },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              className="custom-segmented"
            />
          </div>

          {/* CONTENT */}
          <div className="mt-6">
            {activeTab === "details" && (
              <Card className="rounded-2xl shadow-md">
                <Title level={4}>Description</Title>
                <Text type="secondary">
                  {donationData.description || "No description"}
                </Text>

                <Divider />

                <Title level={4}>Schedule</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card className="rounded-xl">
                      <Text strong>Start Date</Text>
                      <div className="mt-1">
                        📅 {donationData.startDate
                          ? dayjs(donationData.startDate, "MM/DD/YYYY").format(
                              "DD MMM YYYY"
                            )
                          : "N/A"}
                      </div>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card className="rounded-xl">
                      <Text strong>End Date</Text>
                      <div className="mt-1">
                        📅 {donationData.endDate
                          ? dayjs(donationData.endDate, "MM/DD/YYYY").format(
                              "DD MMM YYYY"
                            )
                          : "N/A"}
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Divider />

                <Title level={4}>Contact Information</Title>
                <div className="grid gap-2 mt-2">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    📞 {donationData.contact || "N/A"}
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    ✉️ {donationData.email || "N/A"}
                  </div>
                </div>

                <Divider />

                <Title level={4}>Additional Info</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card className="rounded-xl">
                      <Text strong>Donation ID</Text>
                      <div className="mt-1">
                        {donationData.donation_id}
                      </div>
                    </Card>
                  </Col>

                  <Col span={12}>
                    <Card className="rounded-xl">
                      <Text strong>Created Date</Text>
                      <div className="mt-1">
                        📅 {donationData.raw?.fld_Donation_creaation_date
                          ? dayjs(
                              donationData.raw
                                .fld_Donation_creaation_date,
                              "MM/DD/YYYY"
                            ).format("DD MMM YYYY")
                          : "N/A"}
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>
            )}

            {activeTab === "members" && (
              <Card className="rounded-2xl shadow-md">
                <Table
                  rowKey={(record, index) => index}
                  dataSource={donatedMembers}
                  pagination={{
                    pageSizeOptions: ["5", "10", "25"],
                    showSizeChanger: true,
                    defaultPageSize: 5,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  columns={[
                    {
                      title: "Sl No",
                      key: "slNo",
                      render: (_, __, index) => index + 1,
                    },
                    {
                      title: "Name",
                      dataIndex: "name",
                    },
                    {
                      title: "Phone",
                      dataIndex: "phone",
                    },
                    {
                      title: "Email",
                      dataIndex: "email",
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      render: (val) => `₹ ${val}`,
                    },
                    {
                      title: "Date",
                      dataIndex: "date",
                      render: (val) =>
                        val
                          ? dayjs(val, "MM/DD/YYYY").format("DD MMM YYYY")
                          : "-",
                    },
                  ]}
                />
              </Card>
            )}
          </div>
        </Content>
      </Layout>
    </div>
  );
};

export default DonationDetails;